/**
 * save-pill.js — row 1, centre-right: "is my work safe".
 *
 * The pill, its history popover, and the presence stack.
 *
 * ── The session timer is out of the pill, not out of the product ──────────
 * `ui/toolbar.js` concatenated save state with an active-editing stopwatch
 * (`Saved 3m ago · 42 min`), so the product's only "is my work safe" indicator
 * spent half its width on a number nobody asked for and which changed every 30
 * seconds whether anything happened or not. Contracts §6 removed it, and
 * `_formatSessionDuration` and its always-on `setInterval` are still gone.
 *
 * The READING came back on 2026-08-27, by request. It moved again the same day,
 * from the popover's "This session" group onto the PILL'S TOOLTIP, which is
 * what "on the badge" can mean without re-earning §6's deletion: the label
 * stays single-axis, so a glance still answers only "is my work safe", while
 * the number is one hover away.
 *
 * Three things stop it re-earning that deletion. `shared/session-clock.js`
 * counts only VISIBLE time, so a tab hidden overnight accrues nothing. Nothing
 * owns a timer any more — the tooltip is recomputed at pointerenter/focus, and
 * a tooltip that is closed has no stale value to show, so the 30s popover
 * redraw is gone too. And it never enters the LABEL, which is the part §6 was
 * actually about: an indicator that changes while the status does not is one
 * people stop reading, and "Saved · 9 min" reads as "saved 9 minutes ago".
 *
 * ── Two axes (CMD-4) ──────────────────────────────────────────────────────
 * The table itself is `save-state.js`, pure and exhaustively tested. This file
 * only tracks WHICH axis values are current and renders what the table says.
 * That split is deliberate: the precedence ladder is the part with a wrong
 * answer that reassures ("Up to date", "Saved") and it must not be re-derived
 * inside a DOM method where only three of its twelve combinations get exercised.
 *
 * The `_statusErrorToken` guard sits ABOVE the table as a transient override.
 * `report-error:status` is not a save state — it is "an unrelated action just
 * failed, borrow the status line" — and its 8-second restore must not write a
 * stale label back over a newer one. That race is real; the guard ports across
 * from the old toolbar unchanged.
 *
 * ── The popover is a HUB, not a chronology ────────────────────────────────
 * Session history, checkpoints and publishes have different persistence and
 * different restore semantics, so they are three groups and are never
 * interleaved. Their counts arrive through an INJECTED seam
 * (`setSavePopoverSources`) rather than an import: `revisionStore` lives in
 * `shell.js`, and a command-bar module that imports the shell is unloadable
 * under node. With no sources wired the groups still render their heading and
 * their action, which is the honest degradation — a missing count is not a
 * missing route.
 *
 * **Peer edits are cut.** There is no persisted collaboration activity log to
 * draw them from; the prototype's row was fiction, and shipping it would mean
 * inventing a data source.
 *
 * ── The presence mount is explicit ────────────────────────────────────────
 * `collab-ui.js` used to find its avatar host by querying `.pb-toolbar-left`
 * and inserting before `#pb-auth-chip` — two structural assumptions about a bar
 * that is being rebuilt. It now asks the shell for a named mount, and this is
 * the element that answers.
 */

import { SAVE_LABELS } from '../../shared/ui-vocabulary.js';
import { eventBus } from '../../core/events.js';
import { createButton } from '../components.js';
import { icon } from '../icons.js';
import { openPopoverMenu } from '../popover-menu.js';
import { createSessionClock } from '../../shared/session-clock.js';
import { showToast } from '../toast.js';
import { deriveSaveView, SAVE_COMMANDS } from './save-state.js';

/**
 * Cheap summaries for the popover's three groups. Injected, never imported.
 * Each returns `{count, detail}`, a Promise for one, or null; null renders the
 * group's heading and action without a count, which is correct rather than
 * degraded. Published history is remote, so the async shape is intentional.
 */
let _sources = {
  sessionHistory: null,
  checkpoints: null,
  published: null
};

/** Wired once from `shell.js`, which owns `revisionStore` and the store. */
export function setSavePopoverSources(sources = {}) {
  _sources = { ..._sources, ...sources };
}

/** Test hook — restores the default (unwired) state. */
export function _resetSavePopoverSources() {
  _sources = { sessionHistory: null, checkpoints: null, published: null };
}

function readSource(name) {
  const fn = _sources[name];
  if (typeof fn !== 'function') return null;
  try {
    const value = fn();
    if (value && typeof value.then === 'function') {
      return Promise.resolve(value)
        .then((summary) => summary || null)
        .catch(() => null);
    }
    return value || null;
  } catch {
    // A popover that throws because one of three summaries failed is worse
    // than a popover with two summaries. Documented expected-flow fallback.
    return null;
  }
}

export class SavePill {
  /** @param {HTMLElement} container — the `row1.state` slot */
  constructor(container) {
    this._container = container;
    this._offs = [];
    // ── the two axes ──
    this._local = 'saved';
    // The cloud axis is COMPOSED from three independent facts, because each has
    // its own owner: sync progress comes from the shell's sync loop
    // (`cloud-sync:state`), an expired session from the auth handler, and
    // connectivity from the browser. `_effectiveCloud()` ranks them.
    this._cloudSync = 'none';
    this._cloudDetail = '';
    this._authPaused = false;
    this._offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    // ── the transient override that sits above them ──
    this._statusMessage = null;
    this._statusErrorToken = 0;
    this._lastSavedAt = null;
    this._lastSaveToken = 0;
    this._fadeTimer = null;
    // Active session time. The clock itself owns no timer — `visibilitychange`
    // is event-driven and the popover reads it on open — so nothing here ticks
    // while nobody is looking. That, and living in the popover instead of the
    // pill, are the two reasons the original was removed (contracts §241).
    this._sessionClock = createSessionClock({
      visible: typeof document === 'undefined' || document.visibilityState !== 'hidden'
    });
    this._render();
    this._listen();
  }

  destroy() {
    clearTimeout(this._fadeTimer);
    this._fadeTimer = null;
    for (const off of this._offs) if (typeof off === 'function') off();
    this._offs = [];
    if (this._onOnline) {
      window.removeEventListener('online', this._onOnline);
      window.removeEventListener('offline', this._onOffline);
      this._onOnline = null;
      this._onOffline = null;
    }
    if (this._onVisibility) {
      document.removeEventListener('visibilitychange', this._onVisibility);
      this._onVisibility = null;
    }
  }

  /** For tests. */
  get sessionClock() {
    return this._sessionClock;
  }

  /** The element `collab-ui.js` renders the avatar stack into. */
  get presenceMount() {
    return this._presence;
  }

  /** For tests and the geometry gate. */
  get axes() {
    return { local: this._local, cloud: this._effectiveCloud() };
  }

  /**
   * One cloud value for the table. A sync PROBLEM (conflict, failure, a gone
   * project) outranks an expired session, which outranks being offline, which
   * outranks progress — offline with work pending is still "offline".
   */
  _effectiveCloud() {
    if (['conflict', 'failed', 'unavailable'].includes(this._cloudSync)) return this._cloudSync;
    if (this._authPaused) return 'paused';
    if (this._offline) return 'offline';
    return this._cloudSync;
  }

  _render() {
    this._container.classList.add('pb-cb-state');

    this._dot = document.createElement('span');
    this._dot.className = 'pb-telemetry-dot';
    this._dot.setAttribute('aria-hidden', 'true');

    this._label = document.createElement('span');
    this._label.className = 'pb-telemetry-text';
    this._label.id = 'pb-telemetry-text';
    this._label.textContent = 'Saved';

    this._pill = createButton({
      variant: 'bare',
      className: 'pb-telemetry-chip',
      id: 'pb-telemetry-chip',
      content: [this._dot, this._label],
      ariaHaspopup: 'true',
      ariaExpanded: 'false',
      onClick: () => this._onClick()
    });
    // The pill is the only place a save failure or an expired session is
    // announced without a toast, so it must be a live region.
    this._pill.setAttribute('aria-live', 'polite');
    // The active-session reading lives HERE, on the badge, but only on demand.
    // Recomputed at hover/focus rather than on a timer: the tooltip cannot be
    // read while it is closed, so there is nothing to keep fresh in between —
    // which is how this home costs no interval at all.
    this._pill.addEventListener('pointerenter', () => this._syncTooltip());
    this._pill.addEventListener('focus', () => this._syncTooltip());

    this._presence = document.createElement('div');
    this._presence.className = 'pb-cb-presence';
    this._presence.id = 'pb-presence-mount';

    this._container.append(this._pill, this._presence);
    this._sync();
  }

  /**
   * One `emit`, not a second copy of the precedence ladder. `save-state.js`
   * returns the command NAME, so a table change moves the click with it.
   */
  _onClick() {
    const { click } = this._view();
    if (click === 'command-bar:open-save-popover') this._openPopover();
    else eventBus.emit(click);
  }

  _view() {
    // The transient override wins over both axes while it is live — it is a
    // real failure the user just caused, and it names the action that failed.
    if (this._statusMessage) {
      return {
        tone: 'error',
        label: this._statusMessage,
        short: 'Failed',
        title: this._statusMessage,
        click: 'command-bar:open-save-popover',
        pulsing: false,
        settled: false
      };
    }
    return deriveSaveView({
      local: this._local,
      cloud: this._effectiveCloud(),
      savedAt: this._lastSavedAt,
      detail: this._cloudDetail
    });
  }

  _setLocal(local) {
    this._local = local;
    if (local === 'saved') this._lastSavedAt = Date.now();
    this._sync();
  }

  _listen() {
    const on = (name, fn) => this._offs.push(eventBus.on(name, fn));

    // ── local axis ──
    //
    // state.js emits `autosave:dirty` on the clean→dirty transition. Bump the
    // status token so a pending report-error:status restore cannot write a
    // stale label back over it.
    on('autosave:dirty', () => {
      if (this._local === 'saving') return;
      this._clearStatus();
      this._setLocal('dirty');
    });

    // BC-2: `save:completed` is deliberately NOT a local-axis event any more.
    // Its emitters are a .json download, a .zip download and a manual cloud
    // save — none of which writes this browser's autosave slot — and treating
    // it as one turned "Save failed" into "Saved now" after a download while
    // the disk write was still failing.

    on('autosave:saving', ({ token }) => {
      this._lastSaveToken = token;
      this._clearStatus();
      this._setLocal('saving');
    });

    on('autosave:saved', ({ token }) => {
      if (token < this._lastSaveToken) return;
      this._clearStatus();
      this._setLocal('saved');
    });

    on('autosave:error', ({ token, reason }) => {
      if (token < this._lastSaveToken) return;
      this._clearStatus();
      this._setLocal('error');
      showToast(reason ? `Autosave failed — ${reason}` : 'Autosave failed', {
        type: 'error',
        duration: 8000,
        id: 'autosave-failed',
        action: {
          label: SAVE_LABELS.retry,
          onClick: () => eventBus.emit(SAVE_COMMANDS.retryLocal)
        }
      });
    });

    // ── cloud axis ──
    // The shell's sync loop is the only thing that knows whether a project has
    // an account copy, is waiting for its tick, is uploading, or failed.
    on('cloud-sync:state', ({ state, detail } = {}) => {
      if (!state) return;
      this._cloudSync = state;
      this._cloudDetail = typeof detail === 'string' ? detail : '';
      this._sync();
    });
    on('auth:sync-paused', () => {
      this._authPaused = true;
      this._sync();
    });
    on('auth:sync-resumed', () => {
      this._authPaused = false;
      this._offline = this._isOffline();
      this._sync();
    });

    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      // An expired session outranks connectivity (`_effectiveCloud`): signing
      // in is the action, and it is not available offline either way. Coming
      // back online must not silently clear a paused session — the two are
      // separate facts, so it cannot.
      this._onOnline = () => {
        this._offline = false;
        this._sync();
      };
      this._onOffline = () => {
        this._offline = true;
        this._sync();
      };
      window.addEventListener('online', this._onOnline);
      window.addEventListener('offline', this._onOffline);
    }

    if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
      this._onVisibility = () => {
        this._sessionClock.setVisible(document.visibilityState !== 'hidden');
      };
      document.addEventListener('visibilitychange', this._onVisibility);
    }

    // ── the transient override ──
    on('report-error:status', ({ message }) => {
      const token = ++this._statusErrorToken;
      clearTimeout(this._fadeTimer);
      this._statusMessage = message || 'Action failed';
      this._sync();
      this._fadeTimer = setTimeout(() => {
        // Only the CURRENT status may clear itself. Without the token a stale
        // restore wipes a newer message — the race the old toolbar's
        // `_statusErrorToken` guard existed for, ported unchanged.
        if (this._statusErrorToken !== token) return;
        this._statusMessage = null;
        this._sync();
      }, 8000);
    });

    on('command-bar:open-save-popover', (payload = {}) => this._openPopover(payload));
  }

  _isOffline() {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
  }

  /** A newer state supersedes a pending status restore. */
  _clearStatus() {
    this._statusErrorToken++;
    clearTimeout(this._fadeTimer);
    this._statusMessage = null;
  }

  _sync() {
    const view = this._view();
    this._label.textContent = view.label;
    // BC-10: the narrow bar and the phone decide from these, not from the tone.
    // A label may shrink to a dot ONLY when nothing is wrong or waiting, and a
    // phone shows the short label for everything else.
    this._label.dataset.short = view.short || view.label;
    this._pill.dataset.settled = String(Boolean(view.settled));
    this._pill.dataset.tone = view.tone;
    this._pill.classList.toggle('pb-telemetry-pulsing', view.pulsing);
    this._syncTooltip(view);
  }

  /**
   * Save state, then the active-session reading, on the badge's tooltip.
   *
   * The LABEL stays single-axis — `Saved · 42 min` is what Contracts §6 deleted,
   * because a status indicator that changes while the status does not is one
   * people stop reading, and "Saved · 9 min" invites the exact wrong reading
   * ("saved 9 minutes ago"). The tooltip has neither problem: it is only ever
   * read deliberately, and it says which number is which.
   */
  _syncTooltip(view = this._view()) {
    const active = this._sessionClock?.text?.();
    const title = active ? `${view.title} · active for ${active}` : view.title;
    this._pill.dataset.tooltip = title;
    this._pill.setAttribute('aria-label', title);
  }

  // ── the popover hub ───────────────────────────────────────────────────────

  /**
   * @param {{returnFocusTo?: HTMLElement}} [opts] BC-10: the control that asked.
   *   On a phone that is the More button — the pill it would otherwise return
   *   focus to may be off screen.
   */
  _openPopover({ returnFocusTo = null } = {}) {
    const groups = [
      // BC-7: both groups open the ONE History surface (the timeline over every
      // source). "Checkpoints" used to open the Revision History modal — a
      // second surface with a confirm dialog and no Undo — for the same
      // records the timeline already lists and restores.
      {
        id: 'session',
        heading: 'This session',
        blurb: 'Every edit since the page loaded. Lost on reload.',
        summary: readSource('sessionHistory'),
        action: {
          label: SAVE_LABELS.fullHistory,
          command: 'toolbar:open-history-panel',
          payload: { scope: 'session' },
          glyph: 'history'
        }
      },
      {
        id: 'named',
        heading: 'Named versions',
        blurb: 'Versions you name are kept with the project.',
        summary: readSource('checkpoints'),
        action: {
          label: SAVE_LABELS.checkpoint,
          command: 'toolbar:open-history-panel',
          payload: { focusCheckpoint: true },
          glyph: 'revision'
        }
      },
      {
        id: 'published',
        heading: 'Published',
        loading: 'Loading published history…',
        blurb: 'What your visitors are seeing.',
        summary: readSource('published'),
        action: {
          label: SAVE_LABELS.review,
          command: 'toolbar:review-changes',
          glyph: 'eye'
        }
      }
    ];

    const status = this._statusGroup();

    openPopoverMenu(this._pill, {
      owner: 'command-bar-save',
      align: 'right',
      width: 300,
      menuLabel: SAVE_LABELS.history,
      className: 'pb-cb-save-popover',
      // BC-10: a phone gets a sheet. Anchored to the pill it opened as a desktop
      // popover in the top-left corner, over a pill the phone does not show.
      phoneSheet: 'auto',
      restoreFocus: returnFocusTo
        ? ({ menu }) => {
            if (menu.contains(document.activeElement) && returnFocusTo.isConnected) {
              returnFocusTo.focus();
            }
          }
        : undefined,
      // `render`, not `items[]`: the primitive has no group headings, and three
      // groups with different restore semantics presented as one flat list is
      // exactly the interleaving the contract forbids.
      render: ({ close }) => {
        const frag = document.createDocumentFragment();
        if (status) frag.appendChild(this._renderStatusGroup(status, close));
        for (const group of groups) frag.appendChild(this._renderGroup(group, close));
        frag.appendChild(this._renderBackupGroup(close));
        return frag;
      }
    });
  }

  /**
   * BC-2. When something is wrong, the popover leads with it: what failed, the
   * retry that retries THAT, and — for a local failure only — a recovery copy
   * offered as a separate outcome. The two used to be one button: "Retry"
   * downloaded a file.
   */
  _statusGroup() {
    const view = this._view();
    if (view.click === SAVE_COMMANDS.retryLocal) {
      return {
        heading: 'Your latest changes are not saved',
        blurb: 'Retry saving on this device, or download a recovery copy to keep them safe.',
        actions: [
          { label: SAVE_LABELS.retry, command: SAVE_COMMANDS.retryLocal, glyph: 'history' },
          { label: SAVE_LABELS.recoveryCopy, command: 'toolbar:quick-save', glyph: 'quick' }
        ]
      };
    }
    if (view.click === SAVE_COMMANDS.retryCloud) {
      return {
        heading: 'Cloud sync failed',
        blurb: view.title,
        actions: [
          { label: SAVE_LABELS.retrySync, command: SAVE_COMMANDS.retryCloud, glyph: 'history' }
        ]
      };
    }
    if (view.click === SAVE_COMMANDS.resolveConflict) {
      return {
        heading: 'Sync conflict',
        blurb: view.title,
        actions: [
          {
            label: SAVE_LABELS.resolveConflict,
            command: SAVE_COMMANDS.resolveConflict,
            glyph: 'history'
          }
        ]
      };
    }
    return null;
  }

  _renderStatusGroup(status, close) {
    const section = document.createElement('section');
    section.className = 'pb-cb-save-group pb-cb-save-status';
    section.dataset.group = 'status';
    const heading = document.createElement('h3');
    heading.className = 'pb-cb-save-heading';
    heading.textContent = status.heading;
    const blurb = document.createElement('p');
    blurb.className = 'pb-cb-save-blurb';
    blurb.textContent = status.blurb;
    section.append(heading, blurb);
    for (const action of status.actions) {
      section.appendChild(
        createButton({
          variant: 'bare',
          className: 'pb-cb-menu-item',
          role: 'menuitem',
          icon: icon(action.glyph, { size: 'sm' }),
          label: action.label,
          onClick: () => {
            close();
            eventBus.emit(action.command);
          }
        })
      );
    }
    return section;
  }

  _renderGroup(group, close) {
    const section = document.createElement('section');
    section.className = 'pb-cb-save-group';
    section.dataset.group = group.id;

    const heading = document.createElement('h3');
    heading.className = 'pb-cb-save-heading';
    heading.textContent = group.heading;

    const blurb = document.createElement('p');
    blurb.className = 'pb-cb-save-blurb';
    section.append(heading, blurb);
    let count = null;
    const renderSummary = (summary) => {
      // The summary when one is wired, the standing explanation otherwise. A
      // group with no count still has a route, and saying what it IS beats
      // saying nothing.
      blurb.textContent = summary?.detail || group.blurb;
      count?.remove();
      count = null;
      if (Number.isInteger(summary?.count)) {
        count = document.createElement('span');
        count.className = 'pb-cb-save-count';
        count.textContent = String(summary.count);
        heading.appendChild(count);
      }
    };
    if (group.summary && typeof group.summary.then === 'function') {
      blurb.textContent = group.loading || 'Loading…';
      void group.summary.then((summary) => {
        if (section.isConnected) renderSummary(summary);
      });
    } else {
      renderSummary(group.summary);
    }

    section.appendChild(
      createButton({
        variant: 'bare',
        className: 'pb-cb-menu-item',
        role: 'menuitem',
        // Every row in this popover carries a glyph — the two backup rows always
        // did, and without one here the group actions read as a different kind
        // of control than the thing directly below them.
        icon: icon(group.action.glyph, { size: 'sm' }),
        label: group.action.label,
        onClick: () => {
          close();
          eventBus.emit(group.action.command, group.action.payload);
        }
      })
    );
    return section;
  }

  /**
   * "Download a backup ▸" FLATTENED to two rows.
   *
   * `openPopoverMenu` has no submenu support — verified, not assumed — so a
   * nested affordance would have to be hand-rolled inside a shared primitive
   * used by many surfaces. Two labelled rows say more than a submenu did
   * anyway: the choice is fast-vs-everything, and the labels now say so.
   */
  _renderBackupGroup(close) {
    const section = document.createElement('section');
    section.className = 'pb-cb-save-group';
    section.dataset.group = 'backup';

    const heading = document.createElement('h3');
    heading.className = 'pb-cb-save-heading';
    heading.textContent = 'Download a backup';
    section.appendChild(heading);

    // The `data-save-action` attributes are LITERAL and load-bearing: the
    // census parity gate reads them out of the source, and these two rows are
    // now the only authored home of `quick` and `full`.
    // BC-2: the rows say what each backup CONTAINS. "Download .json" and
    // "Download .zip" asked the user to know that the JSON keeps images as
    // references and the ZIP carries the media. The extension stays, as the
    // secondary fact it is.
    for (const [label, hint, glyph, command, action] of [
      [SAVE_LABELS.backupComplete, '.zip', 'full', 'toolbar:full-save', 'data-save-action="full"'],
      [SAVE_LABELS.backupData, '.json', 'quick', 'toolbar:quick-save', 'data-save-action="quick"']
    ]) {
      const hintEl = document.createElement('span');
      hintEl.className = 'pb-cb-menu-item-hint';
      hintEl.textContent = hint;
      const btn = createButton({
        variant: 'bare',
        className: 'pb-cb-menu-item',
        role: 'menuitem',
        icon: icon(glyph, { size: 'sm' }),
        label,
        content: hintEl,
        onClick: () => {
          close();
          eventBus.emit(command);
        }
      });
      const [name, value] = action.split('=');
      btn.setAttribute(name, value.replace(/"/g, ''));
      section.appendChild(btn);
    }
    return section;
  }
}
